import { z } from 'zod';
const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
    const date = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}, 'Expected a real calendar date');
export const allocationReadinessWeekStartSchema = isoDateSchema.refine((value) => (new Date(`${value}T00:00:00.000Z`).getUTCDay() === 1), 'weekStart must be a Monday');
export const allocationMatchingReadinessQuerySchema = z.object({
    weekStart: allocationReadinessWeekStartSchema,
    cohortNumber: z.coerce.number().int().positive().optional(),
    cohortYear: z.coerce.number().int().min(2000).max(2200).optional(),
}).strict().superRefine((value, context) => {
    if ((value.cohortNumber === undefined) !== (value.cohortYear === undefined)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'cohortNumber and cohortYear must be supplied together',
        });
    }
});
export const allocationReadinessProviderSchema = z.object({
    provider: z.enum(['allocation', 'doneverse', 'keap', 'typeform']),
    state: z.enum([
        'ok',
        'disabled',
        'not_configured',
        'auth_failed',
        'rate_limited',
        'unavailable',
    ]),
    checkedAt: z.string().datetime().nullable(),
    detail: z.string().max(240).nullable(),
}).strict();
export const allocationReadinessFindingSchema = z.object({
    code: z.string().min(1).max(100),
    class: z.enum([
        'hard_blocker',
        'excluded_by_current_logic',
        'warning_or_capacity_risk',
        'synchronization_gap',
        'missing_at_source',
        'code_or_configuration_defect',
    ]),
    reconciliationState: z.enum([
        'in_sync',
        'missing_at_source',
        'present_at_source_missing_in_allocation',
        'conflicting_values',
        'ambiguous_source_identity',
        'stale_sync',
        'pending_automation',
        'failed_automation',
        'outside_retrieval_window',
        'provider_unavailable',
        'external_check_unavailable',
    ]),
    severity: z.enum(['blocker', 'warning', 'info']),
    source: z.enum(['allocation', 'doneverse', 'keap', 'typeform', 'matching']),
    field: z.string().max(100).nullable(),
    allocationState: z.enum(['present', 'missing', 'defaulted', 'not_projected', 'unknown']),
    observedAt: z.string().datetime().nullable(),
    detail: z.string().min(1).max(500),
    remediation: z.string().min(1).max(500),
}).strict();
export const allocationReadinessReconciliationIssueSchema = z.object({
    founderRef: z.string().min(1).max(100),
    founderDisplayName: z.string().min(1).max(200),
    code: z.enum([
        'allocation_onboarding_date_missing',
        'allocation_onboarding_date_conflict',
        'keap_only',
        'allocation_only',
        'ambiguous_source_identity',
    ]),
    severity: z.enum(['blocker', 'warning', 'info']),
    allocationOnboardingDate: isoDateSchema.nullable(),
    keapOnboardingDate: isoDateSchema.nullable(),
    detail: z.string().min(1).max(500),
    remediation: z.string().min(1).max(500),
}).strict();
export const allocationReadinessEvidenceSchema = z.object({
    allocation: z.object({
        present: z.boolean(),
        externalIdPresent: z.boolean(),
    }).strict(),
    doneverse: z.object({
        identity: z.enum(['linked', 'unlinked', 'missing', 'unknown']),
        workspace: z.enum(['present', 'missing', 'unknown']),
    }).strict(),
    keap: z.object({
        state: z.enum(['present', 'missing', 'unknown']),
    }).strict(),
    typeform: z.object({
        state: z.enum(['present', 'missing', 'stale', 'unknown']),
        submittedAt: z.string().datetime().nullable(),
    }).strict(),
}).strict();
export const allocationReadinessFounderSlotSchema = z.object({
    founderRef: z.string().min(1).max(100),
    founderDisplayName: z.string().min(1).max(200),
    slot: z.enum(['1st', '2nd', '3rd']),
    queueOutcome: z.enum(['included', 'excluded', 'suppressed']),
    readiness: z.enum(['ready', 'blocked', 'warning', 'not_applicable']),
    eligibleDoerCount: z.number().int().nonnegative().nullable(),
    findings: z.array(allocationReadinessFindingSchema),
    evidence: allocationReadinessEvidenceSchema,
}).strict();
export const allocationReadinessCohortSchema = z.object({
    number: z.number().int().positive(),
    year: z.number().int().min(2000).max(2200),
    label: z.string().min(1).max(50),
    source: z.enum(['explicit', 'current_setting']),
}).strict();
export const allocationReadinessSummarySchema = z.object({
    matchingCandidateSlots: z.number().int().nonnegative(),
    includedCandidateSlots: z.number().int().nonnegative(),
    totalReady: z.number().int().nonnegative(),
    totalBlocked: z.number().int().nonnegative(),
    totalExcluded: z.number().int().nonnegative(),
    totalSuppressed: z.number().int().nonnegative(),
    totalWarnings: z.number().int().nonnegative(),
    doersAvailable: z.number().int().nonnegative(),
    allocationMissingOnboardingDate: z.number().int().nonnegative(),
    keapCohortContacts: z.number().int().nonnegative().nullable(),
    keapOnly: z.number().int().nonnegative().nullable(),
    allocationOnly: z.number().int().nonnegative().nullable(),
    onboardingDateConflicts: z.number().int().nonnegative().nullable(),
}).strict();
export const allocationMatchingReadinessResponseSchema = z.object({
    contractVersion: z.literal('2'),
    generatedAt: z.string().datetime(),
    weekStart: allocationReadinessWeekStartSchema,
    cohort: allocationReadinessCohortSchema,
    providers: z.array(allocationReadinessProviderSchema),
    cohortCompleteness: z.enum(['complete', 'incomplete', 'unavailable']),
    cohortScannedAt: z.string().datetime().nullable(),
    summary: allocationReadinessSummarySchema,
    founderSlots: z.array(allocationReadinessFounderSlotSchema),
    reconciliationIssues: z.array(allocationReadinessReconciliationIssueSchema),
}).strict();
//# sourceMappingURL=allocation-readiness.js.map